import React from "react";
import { Box, Button, Dialog, Grid, Stack, styled, Alert } from "@mui/material";
import MuiTextField from "@mui/material/TextField";
import { Formik } from "formik";
import { useSnackbar } from "notistack";

import * as Yup from "yup";
import { H4 } from "app/components/Typography";
import useLoanType from "../hooks/useLoanType";

const TextField = styled(MuiTextField)({ marginBottom: 16 });

const validationSchema = Yup.object().shape({
    Code: Yup.string()
        .required("Code is required!")
        .matches(/^[a-zA-Z]{2}$/, 'Please enter exactly 2 alphabetic characters'),
    Description: Yup.string()
        .required("Description is required!")
        .max(99, 'Must be less than 100 characters')
        .matches(/^[a-zA-Z0-9\s-]+$/, "Only letters, numbers, spaces, and hyphens are allowed!")
});

export default function LoanTypeEditorDialog({ open, handleClose, loantype }) {

    const {
        roles,
        handleSave,
        showSuccessAlert,
    } = useLoanType();
    const isNew = !loantype;
    const { enqueueSnackbar } = useSnackbar();

    const initialValues = {
        ID: loantype?.id || "",
        Code: loantype?.code.trim() || "",
        Description: loantype?.description.trim() || "",
        StatusLUId: loantype?.statusLUId || ""
    };

    const handleFormSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
        let response;
        if (isNew) {
            response = await handleSave(values, isNew, setFieldError);
        }
        else {
            response = await handleSave(values, null, setFieldError);
        }
        setSubmitting(false);
        if (!response) {
            enqueueSnackbar("Loan Type saved successfully", { variant: "success" });
            resetForm();
            handleClose();
        }
        else {
            enqueueSnackbar("Failed to save Loan Type", { variant: "error" });
        }
    };

    return (
        <Dialog onClose={handleClose} open={open}>
            <Box p={3}>
                <H4 sx={{ mb: "20px" }}>{isNew ? "Add Loan Type" : "Update Loan Type"}</H4>
                {showSuccessAlert && (
                    <Alert variant="filled" severity="success">
                        Saved Successfully.
                    </Alert>
                )}
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleFormSubmit}
                    enableReinitialize
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        name="Code"
                                        label="Code"
                                        value={values.Code}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={touched.Code && Boolean(errors.Code)}
                                        helperText={touched.Code && errors.Code}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={10}>
                                    <TextField
                                        fullWidth
                                        name="Description"
                                        label="Description"
                                        value={values.Description}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={touched.Description && Boolean(errors.Description)}
                                        helperText={touched.Description && errors.Description}
                                    />
                                </Grid>
                            </Grid>

                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Button variant="contained" color="primary" type="submit">
                                    Save
                                </Button>
                                <Button variant="outlined" color="secondary" onClick={handleClose}>
                                    Cancel
                                </Button>
                            </Stack>
                        </form>
                    )}
                </Formik>
            </Box>
        </Dialog>
    );
}
